import React, { useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { proposedReplyKey, useConversationStore } from '@/stores/conversationStore';
import { MAX_PROPOSED_REPLIES } from '@/utils/storedMessage';
import type { Message } from '@/types';

/** What the open conversation lets the viewer do with a proposed reply. Absent = the viewer may not act at all. */
export interface ProposedReplyActions {
  /**
   * Send reply `index` of note `noteMessageId` to the customer. The SERVER sends the text it stored; nothing typed or held
   * here travels. Rejects when nothing was sent.
   */
  send: (noteMessageId: string, index: number) => Promise<void>;
  /** Put the reply's text into the answer bar's composer for the agent to change - nothing is sent. */
  edit: (text: string) => void;
}

/**
 * 2026-10-28 (operator) - the replies the bot proposes under its consultation note, up to three, each one click from the
 * customer ("Send to customer") or into the composer ("Edit").
 *
 * Rendered ONLY when the caller passes actions, which ChatView does only when the signed-in agent may act on this
 * conversation (the same gate as the answer bar's composer, ADR-033): a read-only viewer, a closed conversation or a
 * teammate's conversation shows no reply at all.
 *
 * While one reply is being sent every Send of this note is disabled (one click is one send); after the hub accepts it the
 * used one says "Sent" and the note's other Send buttons stay disabled, so an alternative cannot follow by accident. Edit
 * stays available. A failed send says so in place (role=alert) and enables the buttons again.
 *
 * The text is rendered as TEXT (React escapes it), never as HTML, with its own direction per paragraph.
 */
export const ProposedReplies: React.FC<{ note: Message; replies: string[]; actions: ProposedReplyActions }> = ({
  note,
  replies,
  actions,
}) => {
  const { t } = useT();
  const sentIndex = useConversationStore((s) => s.sentProposedReplies[proposedReplyKey(note.sessionId, note.messageId)]);
  const markSent = useConversationStore((s) => s.markProposedReplySent);
  const [sendingIndex, setSendingIndex] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  // Positions are the server's indices: never filter before mapping (a blank entry is skipped in place, not removed).
  const shown = replies
    .slice(0, MAX_PROPOSED_REPLIES)
    .map((text, index) => ({ text: typeof text === 'string' ? text : '', index }))
    .filter((r) => r.text.trim().length > 0);
  if (shown.length === 0) return null;

  const alreadySent = typeof sentIndex === 'number';
  const busy = sendingIndex !== null;

  const send = async (index: number) => {
    if (busy || alreadySent) return;
    setSendingIndex(index);
    setFailed(false);
    try {
      await actions.send(note.messageId, index);
      markSent(note.sessionId, note.messageId, index);
    } catch {
      setFailed(true);
    } finally {
      setSendingIndex(null);
    }
  };

  return (
    <div className="ac-proposed" role="group" aria-label={t('pr_group')} data-testid="proposed-replies">
      <div className="ac-proposed-head">{t('pr_head')}</div>
      {shown.map(({ text, index }) => {
        const n = index + 1;
        const isSent = sentIndex === index;
        return (
          <div
            key={index}
            className={'ac-proposed-item' + (isSent ? ' sent' : '')}
            data-testid={`proposed-reply-${index}`}
          >
            <div className="ac-proposed-text" dir="auto">
              {text}
            </div>
            <div className="ac-proposed-actions">
              {isSent ? (
                <span className="ac-proposed-sent" role="status" data-testid={`proposed-reply-sent-${index}`}>
                  <Icon name="check" size={13} />
                  {t('pr_sent')}
                </span>
              ) : (
                <button
                  type="button"
                  className="ac-proposed-send"
                  onClick={() => void send(index)}
                  disabled={busy || alreadySent}
                  aria-label={t('pr_send_aria', { n })}
                  data-testid={`proposed-reply-send-${index}`}
                >
                  <Icon name="send" size={13} className="ac-flip" />
                  {sendingIndex === index ? t('pr_sending') : t('pr_send')}
                </button>
              )}
              <button
                type="button"
                className="ac-proposed-edit"
                onClick={() => actions.edit(text)}
                disabled={busy}
                aria-label={t('pr_edit_aria', { n })}
                title={t('pr_edit_aria', { n })}
                data-testid={`proposed-reply-edit-${index}`}
              >
                <Icon name="pencil" size={14} />
              </button>
            </div>
          </div>
        );
      })}
      {failed && (
        <div className="ac-proposed-error" role="alert" data-testid="proposed-reply-error">
          {t('pr_failed')}
        </div>
      )}
    </div>
  );
};
